import React from "react";
import loadable from "@loadable/component";
import pMinDelay from "p-min-delay";
import Loader from "./shared/Loader";

// Layouts
import Layout2 from "./shared/layouts/Layout2";
import LayoutBlank from "./shared/layouts/LayoutBlank";

// Lazy load component
const lazy = (cb) =>
  loadable(() => pMinDelay(cb(), 200), { fallback: <Loader /> });

export const DefaultLayout = Layout2;

export const titleTemplate = "%s - ProStruct360";

// ---
// Routes
//
// Note: By default all routes use { "exact": true }. To change this
// behaviour, pass "exact" option explicitly to the route object

export const defaultRoute = "/dashboard";
export const routes = [
  {
    path: "/login",
    component: lazy(() => import("./components/authentication/Login")),
    layout: LayoutBlank,
  },
  {
    path: "/sign-up",
    component: lazy(() => import("./components/authentication/SignUp")),
    layout: LayoutBlank,
  },
  {
    path: "/sign-out",
    component: lazy(() => import("./components/authentication/SignOut")),
    layout: LayoutBlank,
  },
  {
    path: "/login/impersonate/:token",
    component: lazy(
      () => import("./components/authentication/ImpersonateUser"),
    ),
    layout: LayoutBlank,
  },
  {
    path: "/dashboard",
    component: lazy(() => import("./components/dashboards/Dashboard")),
  },
  {
    path: "/calendar",
    component: lazy(() => import("./components/calendar/Calendar")),
  },
  {
    path: "/reset-password",
    component: lazy(() => import("./components/authentication/Login")),
    layout: LayoutBlank,
    routeVariables: { showReset: true },
  },
  {
    path: "/quickbooks-connect-disconnect",
    component: lazy(
      () => import("./components/partner/QuickbooksConnectDisconnect"),
    ),
  },
  {
    path: "/quickbooksactivation",
    component: lazy(() => import("./components/partners/QuickbooksActivation")),
  },
  {
    path: "/quickbookscustomers",
    component: lazy(() => import("./components/partners/QuickbooksCustomers")),
  },
  {
    path: "/quickbooksitems",
    component: lazy(() => import("./components/partners/QuickbooksItems")),
  },
  {
    path: "/quickbooksvendors",
    component: lazy(() => import("./components/partners/QuickbooksVendors")),
  },
  {
    path: "/dashboard/:pid",
    component: lazy(() => import("./components/dashboards/Dashboard")),
  },
  {
    path: "/clients",
    component: lazy(() => import("./components/clients/Clients")),
  },
  {
    path: "/clients/:id",
    component: lazy(() => import("./components/clients/Client")),
  },
  {
    path: "/partner/clientpayments",
    component: lazy(() => import("./components/clients/ClientPayments")),
  },
  {
    path: "/partner/receivepayment",
    component: lazy(() => import("./components/clients/ReceivePayment")),
  },
  {
    path: "/addresses",
    component: lazy(() => import("./components/addresses/Addresses")),
  },
  {
    path: "/addresses/:id",
    component: lazy(() => import("./components/addresses/Address")),
  },
  {
    path: "/jobs",
    component: lazy(() => import("./components/jobs/Jobs")),
  },
  {
    path: "/jobtemplates",
    component: lazy(() => import("./components/jobs/JobTemplates")),
  },
  {
    path: "/jobs/0",
    component: lazy(() => import("./components/jobs/CreateJob")),
  },
  {
    path: "/jobs/:id",
    component: lazy(() => import("./components/jobs/Job")),
  },
  {
    path: "/jobs-vendor/:id",
    component: lazy(() => import("./components/jobs/JobVendor")),
  },
  {
    path: "/jobs-customer/:id",
    component: lazy(() => import("./components/jobs/JobCustomer")),
  },
  {
    path: "/jobleads",
    component: lazy(() => import("./components/jobleads/JobLeads")),
  },
  {
    path: "/jobleads/:id",
    component: lazy(() => import("./components/jobleads/LeadQuote")),
  },
  {
    path: "/quotes",
    component: lazy(() => import("./components/jobleads/JobLeads")),
  },
  {
    path: "/quotes/:id",
    component: lazy(() => import("./components/jobleads/LeadQuote")),
  },
  {
    path: "/reports/profitlossreport",
    component: lazy(() => import("./components/reports/ProfitLossReport")),
  },
  {
    path: "/reports/invoicingitems",
    component: lazy(() => import("./components/users/InvoicingVendorItems")),
  },
  {
    path: "/reports/payments",
    component: lazy(() => import("./components/reports/Payments")),
  },
  {
    path: "/reports/pendingbills",
    component: lazy(() => import("./components/reports/PendingBills")),
  },
  {
    path: "/reports/vendorpaymenthistory",
    component: lazy(() => import("./components/users/VendorPaymentHistory")),
  },
  {
    path: "/reports/qbologs",
    component: lazy(() => import("./components/reports/QBOLogs")),
  },
  {
    path: "/support",
    component: lazy(() => import("./components/users/Support")),
  },
  {
    path: "/admin/catalog",
    component: lazy(() => import("./components/admin/catalog/Catalog")),
  },
  {
    path: "/admin/suppliers",
    component: lazy(() => import("./components/admin/suppliers/Suppliers")),
  },
  {
    path: "/admin/trades",
    component: lazy(() => import("./components/admin/trades/Trades")),
  },
  {
    path: "/admin/partners",
    component: lazy(() => import("./components/partners/Partners")),
  },
  {
    path: "/admin/partners/:id",
    component: lazy(() => import("./components/partners/Partner")),
  },
  {
    path: "/admin/users",
    component: lazy(() => import("./components/users/Users")),
  },
  {
    path: "/admin/users/:id",
    component: lazy(() => import("./components/users/User")),
  },
  {
    path: "/admin/emailtemplates",
    component: lazy(() => import("./components/users/EmailTemplates")),
  },
  {
    path: "/admin/vendor/:id/:scroll?",
    component: lazy(() => import("./components/vendors/Vendor")),
  },
  {
    path: "/partner/vendor-payment",
    component: lazy(() => import("./components/vendors/Payments")),
  },
  {
    path: "/partner",
    component: lazy(() => import("./components/partner/Partner")),
  },
  {
    path: "/partner/teamusers",
    component: lazy(() => import("./components/partner/TeamUsers")),
  },
  {
    path: "/partner/vendors",
    component: lazy(() => import("./components/partner/VendorUsers")),
  },
  {
    path: "/admin/billing",
    component: lazy(() => import("./components/admin/billing/Billing")),
  },
  {
    path: "/inboxes",
    component: lazy(() => import("./components/inboxes/Inboxes")),
  },
  {
    path: "/inboxes/:id",
    component: lazy(() => import("./components/inboxes/Inbox")),
  },
  {
    path: "/emails",
    component: lazy(() => import("./components/my-email/MyEmails")),
  },
  {
    path: "/emails-job/:jobIdParam?",
    component: lazy(() => import("./components/my-email/MyEmails")),
  },
  {
    path: "/emails/:id",
    component: lazy(() => import("./components/my-email/EmailView")),
  },
  {
    path: "/emails/compose/:id",
    component: lazy(() => import("./components/my-email/EmailCompose")),
  },
  {
    path: "/quicksignup",
    component: lazy(() => import("./components/authentication/NylasSignUp")),
  },
  {
    path: "/signupresponse",
    component: lazy(() => import("./components/authentication/NylasResponse")),
    layout: LayoutBlank,
  },
  {
    path: "/payment",
    component: lazy(() => import("./components/payment/Payment")),
    layout: LayoutBlank,
  },
  {
    path: "/payment-success",
    component: lazy(() => import("./components/payment/PaymentSuccess")),
    layout: LayoutBlank,
  },
  {
    path: "/partner/:id",
    component: lazy(() => import("./components/partner/Partner")),
  },
  {
    path: "/reload",
    component: lazy(() => import("./components/vendors/Reload")),
  },
  {
    path: "/vendors/0",
    component: lazy(() => import("./components/vendors/CreateVendor")),
  },
  {
    path: "/payment-error",
    component: lazy(() => import("./components/payment/PaymentError")),
    layout: LayoutBlank,
  },
  {
    path: "/test-popup",
    component: lazy(() => import("./components/vendors/Test")),
    layout: LayoutBlank,
  },
];
